import { Button } from "@babackapp/customer-ui/components/button";
import { Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import type {
  BannerToEmbedMessage,
  CartItem,
  EmbedToBannerMessage,
} from "@babackapp/return-widget-components/types/shop-now";

type ShopNowState = {
  orderId: string;
  exchangeCreditAmount: number;
  currency: string;
  /** 1 + tax rate for HT shops; 1 for TTC shops. Scale cartTotal before comparing. */
  taxMultiplier: number;
};

function postToParent(msg: BannerToEmbedMessage) {
  window.parent.postMessage(msg, "*");
}

export function RebuyBanner() {
  const { shopId } = useParams<{ shopId: string }>();
  const [shopNowState, setShopNowState] = useState<ShopNowState | null>(null);
  const [cartItems, setCartItems] = useState<CartItem[]>([]);

  useEffect(() => {
    postToParent({ type: "BANNER_READY" });
  }, []);

  useEffect(() => {
    function onMessage(event: MessageEvent) {
      const msg = event.data as EmbedToBannerMessage;
      if (!msg?.type) return;

      if (msg.type === "SHOP_NOW_INIT") {
        setShopNowState({
          orderId: msg.orderId,
          exchangeCreditAmount: msg.exchangeCreditAmount,
          currency: msg.currency,
          taxMultiplier: msg.taxMultiplier ?? 1,
        });
      }

      if (msg.type === "CART_UPDATE") {
        setCartItems(msg.items);
      }
    }

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, []);

  // Scale cartItems prices (HT from Shopify /cart.js on HT shops) to TTC using taxMultiplier,
  // so that remaining = TTC credit - TTC cart — consistent with exchangeCreditAmount (TTC).
  const cartTotal = cartItems.reduce(
    (sum, item) => sum + item.price * item.quantity * (shopNowState?.taxMultiplier ?? 1),
    0,
  );

  const handleComplete = () => {
    if (!shopNowState) return;
    postToParent({
      type: "SHOP_NOW_COMPLETE",
      items: cartItems,
      orderId: shopNowState.orderId,
    });
  };

  const handleCancel = () => {
    postToParent({ type: "SHOP_NOW_CANCEL" });
  };

  if (!shopNowState) {
    return <div data-baback-banner data-shop-id={shopId} />;
  }

  const { exchangeCreditAmount, currency } = shopNowState;
  const formatter = new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  });
  const fmt = (cents: number) => formatter.format(cents / 100);

  const remaining = exchangeCreditAmount - cartTotal;

  const label = cartTotal > 0
    ? `${fmt(cartTotal)} dans le panier · ${fmt(Math.max(0, remaining))} de crédit restant`
    : `Vous avez ${fmt(exchangeCreditAmount)} de crédit échange à dépenser`;

  return (
    <div
      data-baback-banner
      data-shop-id={shopId}
      className="flex items-center gap-2 h-14 px-4 bg-white rounded-full w-full"
    >
      <Button variant="secondary" size="icon" onClick={handleCancel}>
        <Trash2 />
      </Button>

      <span className="flex-1 text-sm font-medium text-center truncate">
        {label}
      </span>

      <Button
        size="sm"
        disabled={cartItems.length === 0}
        onClick={handleComplete}
      >
        Continuer
      </Button>
    </div>
  );
}
